import React, { useEffect, useState } from 'react';
import { Alert, Card, Statistic, Table, Tag, Row, Button, Segmented, Tooltip, message, Modal, DatePicker, Checkbox, Dropdown, Input } from 'antd';
import { CloseCircleOutlined, AuditOutlined, HourglassOutlined, LoadingOutlined, ScheduleOutlined, PlusCircleOutlined, SyncOutlined, CloseOutlined, ProfileOutlined, ClockCircleOutlined, CalendarOutlined, InfoCircleOutlined, WarningOutlined, FolderOpenOutlined, InfoCircleTwoTone, EditOutlined, EyeOutlined, } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { useNavigate, Link } from 'react-router-dom';
import DealEconomicsSummariesTable from '../components/DealEconomicsSummariesTable';
import { getDealsLastSyncInfo, getHomePageStatistics, getPricingDeals, getPricingDealVersions, getPricingTemplates, refreshDeals } from '../../services/Apis';
import { useUserContext } from '../../context/UserContext';
import { stat } from 'fs';
import LogoutPopUp from '../../components/LogoutPopUp';
import dayjs from "dayjs";
import NoPricingDealsTable from '../components/NoPricingDealsTable';
import AllPricingDealsTable from '../components/AllPricingDealsTable';
import { generateTableColumnFilters } from '../services/services';
import { currencySymbolsMap } from '../../context/CurrencyContext';
import DeletePricingTemplateButton from '../components/DeletePricingTemplateButton';
import CopyPricingTemplateButton from '../components/CopyPricingTemplateButton';

const { Search } = Input;


interface DataType {
    key: string,
    dealId: string,
    client: string,
    dealName: string,
    startDate: string,
    Stage: string,
    latestVersion: string[],
    dealAmount: string,
    dealOwner: string,
    odeStatus: string,
    updatedOn: string,
    HubSpotLink: string,
    canEditPricing: boolean,
    isOwner: boolean,
    isPricingContributor: boolean,
    isDealUpdatedAfterLock: boolean,
    workAhead: string,
    unlockRequestReason: string,
    unlockComments?: string
    currency: string
}

interface DealData {
    deal_id: number,
    deal_name: string,
    deal_owner: string,
    deal_stage: string,
    start_date: string,
    hubspot_link: string,
    final_version_id: number,
    final_version_name: string,
    is_version_locked: boolean,
    pricing_deal_type: string,
    deal_amount: number,
    client_name: string,
    can_edit_pricing: boolean,
    is_pricing_contributor: boolean,
    is_owner: boolean,
    is_updated_after_lock: boolean,
    updated_on: string,
    work_ahead: string,
    unlock_request_reason: string,
    unlock_comments?: string
    currency: string
}


interface TemplateLinkedDeal {
    deal_id: number;
    deal_name?: string | null;
    hubspot_link: string;
}

interface TemplateDataType {
    linkedDeals: TemplateLinkedDeal[],
    associatedDealCount: number,
    updatedOnTimestamp: number,
    canEdit: boolean,
    canCopy: boolean,
    canDelete: boolean,
    deleteBlockReason?: string,
    key: string,
    templateId: string,
    templateName: string,
    templateDescription: string,
    projectStartDate: string,
    engagementType: string,
    durationInMonths: string,
    createdOn: string,
    updatedOn: string,
    createdByName: string,
    createdByEmail: string,
    updatedByName: string,
    updatedByEmail: string
}

interface TemplateData {
    linked_deals?: TemplateLinkedDeal[];
    associated_deal_count?: number;
    can_edit?: boolean;
    can_copy?: boolean;
    can_delete?: boolean;
    delete_block_reason?: string;
    template_id: number;
    template_name: string;
    template_description: string;
    project_start_date: string;
    engagement_type: string;
    duration_in_months: number;
    created_on: string;
    updated_on: string;
    created_by_email: string;
    updated_by_email: string;
    created_by_name: string;
    updated_by_name: string;
}


const HomePage = () => {
    const [tokenExpired, setTokenExpired] = useState<boolean>(false);
    const navigate = useNavigate(); // React Router hook for navigation

    const handleNavigate = (url='') => {
        navigate(url ?? '/version-management');  // Navigate to /create-pricing page
    };

    const [statistics, setStatistics] = React.useState<Array<object>>([{}]);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [templateSearchTerm, setTemplateSearchTerm] = React.useState<string>("");
    const [deals, setDeals] = React.useState<DataType[]>([]);
    const [templates, setTemplates] = React.useState<TemplateDataType[]>([]);
    const [filteredDeals, setFilteredDeals] = React.useState(deals);
    const [filteredTemplates, setFilteredTemplates] = React.useState(templates);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [expandedRowKeys, setExpandedRowKeys] = useState<any[]>([]);
    const [selectedTab, setSelectedTab] = React.useState<string>('My Deals')
    const [lastSyncTime, setLastSyncTime] = React.useState<string>('')
    const [refreshingDeals, setRefreshingDeals] = useState<boolean>(false);
    const [odeFilter, setOdeFilter] = React.useState<string>('');
    const [selectedStatistic, setSelectedStatistic] = React.useState<string>('');
    const [refreshingDeal, setRefreshingDeal] = React.useState<string>('');
    const [refreshDealsModalOpen, setRefreshDealsModalOpen] = React.useState<boolean>(false);
    const [refreshTime, setRefreshTime] = React.useState<string | null>(null);
    const [homeError, setHomeError] = React.useState<string>('');
    const [hideClosedDeals, setHideClosedDeals] = useState<boolean>(true);
    const [hideOpenDeals, setHideOpenDeals] = useState<boolean>(false);

    const isSyncAllAllowed = JSON.parse(localStorage.getItem('user_details') as string).features.includes('FE_SYNC_ALL_HUBSPOT_DEALS') ? true : false

    const handleExpand = (key: string) => {
        setExpandedRowKeys((prevExpandedRowKeys) => {
            const index = prevExpandedRowKeys.indexOf(key);
            if (index === -1) {
                return [...prevExpandedRowKeys, key];
            } else {
                return prevExpandedRowKeys.filter((k) => k !== key);
            }
        });
    };

    const { userContext, setUserContext } = useUserContext();

    const handleDealRefresh = async (dealId: string) => {
    setRefreshingDeal(dealId)
    await refreshDeals(parseInt(dealId)).then((resp) => {
        if(resp.error ?? '') {
        message.error(resp.error)
        return
        }
        message.success("Successfully Refreshed the Deal");
        setRefreshingDeal('');
    })
    .catch((error) => {
        setRefreshingDeal('');
        message.error("Failed to Refresh the Deal");
    }).finally(() => setRefreshingDeal(''));
    }

    const deepSearch = (data: DataType[], searchTerm: string): DataType[] => {
        return data.filter((item) =>
            Object.values(item).some(
                (value) =>
                    (typeof value === "string" &&
                        value.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (Array.isArray(value) &&
                        value.some(
                            (element) =>
                                typeof element === "string" &&
                                element.toLowerCase().includes(searchTerm.toLowerCase())
                        )) ||
                    (typeof value === "object" &&
                        value !== null &&
                        deepSearch([value], searchTerm).length > 0)
            )
        );
    };
    const deepSearchTemplate = React.useCallback(function searchTemplates(data: TemplateDataType[], searchTerm: string): TemplateDataType[] {
        return data.filter((item) =>
            Object.values(item).some(
                (value) =>
                    (typeof value === "string" &&
                        value.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (Array.isArray(value) &&
                        value.some(
                            (element) =>
                                typeof element === "string" &&
                                element.toLowerCase().includes(searchTerm.toLowerCase())
                        )) ||
                    (typeof value === "object" &&
                        value !== null &&
                        searchTemplates([value], searchTerm).length > 0)
            )
        );
    }, []);

    const handleSearch = (term: string) => {
        setSearchTerm(term);
    };


    const handleTemplateSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value;
        setTemplateSearchTerm(term);
    }

    useEffect(() => {
        setFilteredTemplates(templateSearchTerm ? deepSearchTemplate(templates, templateSearchTerm) : templates);
    }, [templates, templateSearchTerm, deepSearchTemplate]);

    const generateStageFilters = (data: any[], key: string): any[] => {
        const uniqueValues = Array.from(
            new Set(data.map((item) => item[key].toString()))
        ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        return uniqueValues
        .filter((val) => (selectedTab !== 'My Deals') || !(hideClosedDeals || hideOpenDeals ) || ((hideClosedDeals && !val.includes('Closed')) || (hideOpenDeals && val.includes('Closed'))))
        .map((value) => ({ text: value, value }));
    }

    const columns: TableProps<DataType>['columns'] = [
        // {
        //     title: 'Deal Id',
        //     dataIndex: 'dealId',
        //     key: 'dealId',
        //     render: (text, record) => <a target="_blank" href={record.HubSpotLink}>{text}</a>
        // },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <div style={{display: 'flex', gap: '4px'}}>
                <Tooltip title="Compare Versions">
                    <Button size="small" type="primary" onClick={() => window.location.href = `/version-management?deal_id=${record.dealId}`}>
                        <FolderOpenOutlined />
                    </Button>
                </Tooltip>
                <Tooltip title="Refresh Deal Now">
                    <Button size="small" onClick={() => handleDealRefresh(record.dealId)}>
                        <SyncOutlined spin={refreshingDeal == record.dealId}/>
                    </Button>
                </Tooltip>
            </div>
            ),
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            sorter: (a, b) => a.client.toLowerCase().localeCompare(b.client.toLowerCase()),
            filters: generateTableColumnFilters(deals, 'client'),
            onFilter: (value, record) => record.client === value,
        },
        {
            title: 'Deal Name',
            dataIndex: 'dealName',
            key: 'dealName',
            sorter: (a, b) => a.dealName.toLowerCase().localeCompare(b.dealName.toLowerCase()),
            render: (text, record) =>
                <div style={{ display: 'flex', gap: '8px', alignItems: "center" }}>
                    <a onClick={() => handleExpand(record.key)} href="javascript:void(0);">{text}</a>
                    {record.canEditPricing ?
                        <Tooltip title="You can edit deal"><ScheduleOutlined style={{ color: 'green' }} /></Tooltip> :
                        <Tooltip title="You have only view access"><ScheduleOutlined style={{ color: 'gray' }} /></Tooltip>
                    }
                </div>
            ,
            filters: generateTableColumnFilters(deals, 'dealName'),
            onFilter: (value, record) => record.dealName === value,
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
            defaultSortOrder: 'ascend', // Set default sort order
        },
        {
            title: 'Stage',
            dataIndex: 'Stage',
            key: 'Stage',
            sorter: (a, b) => a.Stage.toLowerCase().localeCompare(b.Stage.toLowerCase()),
            filters: generateStageFilters(deals, 'Stage'),
            onFilter: (value, record) => record.Stage === value,
        },
        // {
        //     title: 'Pricing',
        //     dataIndex: 'latestVersion',
        //     key: 'latestVersion',
        //     render: (_, { latestVersion }) => (
        //         <>
        //             {
        //                 latestVersion[0] ?
        //                     latestVersion.map((tag) => {
        //                         let color = tag.length > 5 ? 'geekblue' : 'green';
        //                         if (tag === 'loser') {
        //                             color = 'volcano';
        //                         }
        //                         return (
        //                             <Tag color={color} key={tag} onClick={handleNavigate} style={{ cursor: "pointer" }}>
        //                                 {tag.toUpperCase()}
        //                             </Tag>
        //                         );
        //                     })
        //                     : null
        //             }
        //         </>
        //     ),
        // },
        {
            title: 'Deal Amount',
            dataIndex: 'dealAmount',
            key: 'dealAmount',
            align: 'right',
            sorter: (a, b) => a.dealAmount.localeCompare(b.dealAmount),
        },
        {
            title: 'Deal Owner',
            dataIndex: 'dealOwner',
            key: 'dealOwner',
            sorter: (a, b) => a.dealOwner.toLowerCase().localeCompare(b.dealOwner.toLowerCase()),
            filters: generateTableColumnFilters(deals, 'dealOwner'),
            onFilter: (value, record) => record.dealOwner === value,
        },
        {
            title: 'ODE Status',
            dataIndex: 'odeStatus',
            key: 'odeStatus',
            sorter: (a, b) => a.odeStatus.toLowerCase().localeCompare(b.odeStatus.toLowerCase()),
            filters: generateTableColumnFilters(deals, 'odeStatus'),
            onFilter: (value, record) => record.odeStatus === value,
        },
        {
            title: 'Pricing Last Updated',
            dataIndex: 'updatedOn',
            key: 'updatedOn',
            // its iso formatted datetime
            sorter: (a, b) => new Date(a.updatedOn).getTime() - new Date(b.updatedOn).getTime(),

        },
        {
            title: 'HubSpot Link',
            dataIndex: 'hubspotLink',
            key: 'hubspotLink',
            render: (text, record) =>
                <div style={{ display: 'flex', gap: '8px', alignItems: "center" }}>
                    <a target="_blank" href={record.HubSpotLink}>{record.dealId}</a>
                </div>
        },
    ];

    const templateColumns: TableProps<TemplateDataType>['columns'] = [
        {
            title: 'Actions',
            fixed: 'left',
            width: 120,
            key: 'action',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <Tooltip title={record.canEdit ? 'View/edit template' : 'View template'}>
                        <Link to={`/pricing-template?templateId=${record.templateId}&templateName=${encodeURIComponent(record.templateName)}`}
                            aria-label={`${record.canEdit ? 'View/edit' : 'View'} template ${record.templateName}`}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, color: '#4096ff' }}>
                            {record.canEdit ? <EditOutlined /> : <EyeOutlined />}
                        </Link>
                    </Tooltip>
                    {record.canCopy && <CopyPricingTemplateButton iconOnly templateId={record.templateId} templateName={record.templateName}
                        onCopied={template => navigate(`/pricing-template?templateId=${template.template_id}&templateName=${encodeURIComponent(template.template_name)}`)}
                        onTokenExpired={() => setTokenExpired(true)} />}
                    <DeletePricingTemplateButton
                        iconOnly
                        templateId={record.templateId}
                        templateName={record.templateName}
                        canDelete={record.canDelete}
                        blockReason={record.deleteBlockReason}
                        onDeleted={() => setTemplates(items => items.filter(item => item.templateId !== record.templateId))}
                        onBlocked={(reason) => setTemplates(items => items.map(item => item.templateId === record.templateId
                            ? { ...item, canDelete: false, deleteBlockReason: reason } : item))}
                        onTokenExpired={() => setTokenExpired(true)}
                    />
                </div>
            ),
        },
//         {
//             title: 'Template Id',
//             dataIndex: 'templateId',
//             key: 'templateId'
//         },
        {
            title: 'Template Name',
            dataIndex: 'templateName',
            key: 'templateName',
            sorter: (a, b) => a.templateName.localeCompare(b.templateName),
            filters: generateTableColumnFilters(templates, 'templateName'),
            onFilter: (value, record) => record.templateName === value,
        },
        {
            title: 'Start Date',
            dataIndex: 'projectStartDate',
            key: 'projectStartDate',
            sorter: (a, b) => new Date(a.projectStartDate).getTime() - new Date(b.projectStartDate).getTime(),
            // defaultSortOrder: 'ascend', // Set default sort order
        },
        {
            title: 'Engagement Type',
            dataIndex: 'engagementType',
            key: 'engagementType',
            // handle engagement type null values and sort by engagement type
            sorter: (a, b) => (a.engagementType || '').localeCompare(b.engagementType || ''),
            // filters: generateTableColumnFilters(templates, 'engagementType'),
            // onFilter: (value, record) => record.engagementType === value,
        },
        {
            title: <Tooltip title="Duration in months">Duration(M)</Tooltip>,
            width: 110,
            dataIndex: 'durationInMonths',
            key: 'durationInMonths',
            align: 'center',
            // sort by number of months
            sorter: (a, b) => Number(a.durationInMonths) - Number(b.durationInMonths),
        },
        {
            title: 'Linked Deal',
            key: 'linkedDeals',
            width: 240,
            render: (_, record) => {
                if (record.associatedDealCount === 0) return <Tag style={{ margin: 0 }}>Not linked</Tag>;
                const [deal, ...otherDeals] = record.linkedDeals;
                if (!deal) return <Tag color="blue" style={{ margin: 0 }}>Linked</Tag>;
                const label = deal.deal_name ? `${deal.deal_name} (${deal.deal_id})` : `Deal ${deal.deal_id}`;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 224 }}>
                        <Tooltip title={`Linked to ${label} — open in HubSpot`}>
                            <a href={deal.hubspot_link} target="_blank" rel="noopener noreferrer"
                                style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {label}
                            </a>
                        </Tooltip>
                        {otherDeals.length > 0 && <Dropdown trigger={['click']} menu={{ items: otherDeals.map(other => ({
                            key: other.deal_id,
                            label: <a href={other.hubspot_link} target="_blank" rel="noopener noreferrer">
                                {other.deal_name ? `${other.deal_name} (${other.deal_id})` : `Deal ${other.deal_id}`}
                            </a>,
                        })) }}>
                            <Button type="text" size="small" style={{ flexShrink: 0 }}
                                aria-label={`Show ${otherDeals.length} more linked deals`}>+{otherDeals.length}</Button>
                        </Dropdown>}
                    </div>
                );
            },
        },
        {
            title: 'Created On',
            dataIndex: 'createdOn',
            key: 'createdOn',
            sorter: (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
            // defaultSortOrder: 'ascend', // Set default sort order
        },
        {
            title: 'Updated On',
            dataIndex: 'updatedOn',
            key: 'updatedOn',
            sorter: (a, b) => a.updatedOnTimestamp - b.updatedOnTimestamp,
            defaultSortOrder: 'descend',
        },
        {
            title: 'Created By',
            dataIndex: 'createdByName',
            key: 'createdByName',
            render: (text, record) => (
                <Tooltip title={record.createdByEmail}>
                    <a>{text}</a>
                </Tooltip>
            ),
            sorter: (a, b) => a.createdByName.localeCompare(b.createdByName),
            filters: generateTableColumnFilters(templates, 'createdByName'),
        },
        {
            title: 'Updated By',
            dataIndex: 'updatedByName',
            key: 'updatedByName',
            render: (text, record) => (
                <Tooltip title={record.updatedByEmail}>
                    <a>{text}</a>
                </Tooltip>
            ),
            sorter: (a, b) => a.updatedByName.localeCompare(b.updatedByName),
            filters: generateTableColumnFilters(templates, 'updatedByName'),
        },

    ];


    // Helper function to format numbers as currency
    const formatCurrency = (amount: number, currency: string): string => {
        try {
            return `${currencySymbolsMap[currency] || '$'} ${Math.round(amount).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
        } catch (error) {
            console.error(error);
            return '$ 0';
        }
    };

    const applyStageFilter = (deals: DataType[]): DataType[] => {
        let filterDeals = deals
        if(hideClosedDeals) {
          filterDeals = filterDeals.filter((deal: DataType) => !deal.Stage.includes('Closed'))
        }
        if(hideOpenDeals) {
          filterDeals = filterDeals.filter((deal: DataType) => deal.Stage.includes('Closed'))
        }
        return filterDeals
    };

    // Function to transform the object
    const transformDeal = (deal: DealData[]): DataType[] => {
        try {
            const transformedData: DataType[] = deal.map((deal: DealData, index: number) => ({
                key: String(index), // Convert deal_id to string
                dealId: String(deal.deal_id), // Assuming 'dealId' should be the final_version_id
                client: deal.client_name,
                dealName: deal.deal_name,
                startDate: deal.start_date ? new Date(deal.start_date).toLocaleDateString('en-US') : deal.start_date, // Format date
                Stage: deal.deal_stage,
                latestVersion: [deal.final_version_name], // Wrap in array
                dealAmount: formatCurrency(deal.deal_amount, deal.currency), // Format amount
                dealOwner: deal.deal_owner,
                odeStatus: deal.is_version_locked ? 'Submitted' : 'Saved, not submitted', // Conditional status
                updatedOn: dayjs(deal.updated_on).tz('America/New_York').format('ddd, MMM D, YYYY h:mm A z'),
                HubSpotLink: deal.hubspot_link,
                canEditPricing: deal.can_edit_pricing,
                isOwner: deal.is_owner,
                isPricingContributor: deal.is_pricing_contributor,
                isDealUpdatedAfterLock: deal.is_updated_after_lock,
                workAhead: deal.work_ahead,
                unlockRequestReason: deal.unlock_request_reason,
                unlockComments: deal.unlock_comments,
                currency: deal.currency
            }))
            return transformedData;
        } catch (error) {
            console.error(error);
            return [];
        }
    };


    // Function to transform the object
    const transformTemplate = (template: TemplateData[]): TemplateDataType[] => {
        try {
            const transformedTemplate: TemplateDataType[] = template.map((template: TemplateData, index: number) => ({
                key: String(template.template_id),
                linkedDeals: template.linked_deals ?? [],
                associatedDealCount: template.associated_deal_count ?? template.linked_deals?.length ?? 0,
                updatedOnTimestamp: Date.parse(template.updated_on) || 0,
                canEdit: template.can_edit === true,
                canCopy: template.can_copy === true,
                canDelete: template.can_delete === true,
                deleteBlockReason: template.delete_block_reason,
                templateId: String(template.template_id), // Assuming 'dealId' should be the final_version_id
                templateName: template.template_name,
                templateDescription: template.template_description,
                projectStartDate: template.project_start_date ? new Date(template.project_start_date).toLocaleDateString('en-US') : template.project_start_date, // Format date
                engagementType: template.engagement_type,
                durationInMonths: String(template.duration_in_months),
                createdOn: template.created_on ? new Date(template.created_on).toLocaleDateString('en-US') : template.created_on, // Format date
                updatedOn: template.updated_on ? new Date(template.updated_on).toLocaleDateString('en-US') : template.updated_on, // Format date
                createdByName: template.created_by_name,
                createdByEmail: template.created_by_email,
                updatedByName: template.updated_by_name,
                updatedByEmail: template.updated_by_email
            }))
            return transformedTemplate;
        } catch (error) {
            console.error(error);
            return [];
        }
    };


    const parseDateTime = (date: string) => {
        const inputDate = new Date(date);

        const options: Intl.DateTimeFormatOptions = {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZoneName: 'short',
            timeZone: 'America/New_York'
        };
        return inputDate.toLocaleString('en-US', options);
    }

    const handleAllDealsRefresh = async () => {
        setRefreshingDeals(true)
        await refreshDeals(null, refreshTime).then((resp) => {
            setRefreshingDeals(false);
            if(resp.error ?? '') {
                message.error(resp.error)
                return
            }
            if(resp.message == 'Already Sync In Progress') {
                message.warning('Already Sync In Progress')
                return
            }
            message.success("Success: Deals will be updated shortly");
            setRefreshDealsModalOpen(false)
        })
        .catch((error) => {
            setRefreshingDeals(false);
            setRefreshDealsModalOpen(false)
            message.error("Failed to Refresh Deals");
        });
    }

    const handleExpandLockDeals = () => {
        let indices_: string[] = [];
        deals.forEach((deal, index) => {
            if (deal.isDealUpdatedAfterLock == true) {
                indices_.push(index.toString());
            }
          });
        setExpandedRowKeys(indices_)
    }

    const handleStatisticFilterApply = (name: string) => {
        setSelectedStatistic(name)
      switch (name) {
        case "New Deals with No Pricing":
          setSelectedTab("Deals with No Pricing");
          break;
        case "Pricing Pending Submission":
          setSelectedTab("Deals with Pricing");
          setOdeFilter("pending");
          break;
        case "Deals Changed in HubSpot After ODE Submission":
          setOdeFilter("lock");
          setSelectedTab("Deals with Pricing");
          handleExpandLockDeals()
          break;
        default:
          setSelectedTab("My Deals");
      }
    };

    const dealsWithFilters = () => {
      return deals.filter(
        (deal) =>
          odeFilter == "" ||
          (odeFilter == "pending" &&
            deal.odeStatus == "Saved, not submitted") ||
          (odeFilter == "lock" && deal.isDealUpdatedAfterLock == true)
      );
    };

    useEffect(() => {
        if(selectedStatistic == 'New Deals with No Pricing') {
            if(selectedTab != 'Deals with No Pricing') {
                setOdeFilter('')
                setSelectedStatistic('')
            }
        } else {
            if(selectedTab != 'Deals with Pricing') {
                setOdeFilter('')
                setSelectedStatistic('')
            }
        }
    }, [selectedTab])
    
    useEffect(() => {
      if (searchTerm) {
        const results = deepSearch(dealsWithFilters(), searchTerm);
        setFilteredDeals(results);
      } else {
        setFilteredDeals(dealsWithFilters());
      }
    }, [searchTerm, odeFilter]);

    useEffect(() => {
      setFilteredDeals(dealsWithFilters());
    }, [odeFilter]);

    useEffect(() => {
        const fetchDealDetails = async () => {
            const deals = await getPricingDeals();
            const transformedDeal: any = transformDeal(deals);
            setDeals(transformedDeal);
            setFilteredDeals(transformedDeal);
            setOdeFilter('')
            setSearchTerm('')
            setSelectedStatistic('')
        }
        if(refreshingDeal == '') {
            fetchDealDetails();
        }
    }, [refreshingDeal])

    useEffect(() => {
        try {
            if (userContext === null) {
                setUserContext(JSON.parse(localStorage.getItem('user_details') as string));
            }
            const fetchHomeScreenDetails = async () => {
                const statistics = await getHomePageStatistics();
                const lastSyncInfo = await getDealsLastSyncInfo();
                const deals = await getPricingDeals();
                let templates = await getPricingTemplates();
                if (templates.error_status === 401) {
                    templates = [];
                }
                if (statistics.error_status === 401 || deals.error_status === 401 || lastSyncInfo.error_status === 401) {
                    setTokenExpired(true);
                }
                const failedSources = [statistics, lastSyncInfo, deals, templates]
                    .filter((source: any) => source?.error_status && source.error_status !== 401);
                if (failedSources.length) {
                    setHomeError('Some deal data could not be loaded. Refresh the page and try again.');
                }
                setStatistics(Array.isArray(statistics) ? statistics : []);
                setLastSyncTime(parseDateTime(lastSyncInfo['last_refreshed_on']))
                const transformedDeal: any = transformDeal(Array.isArray(deals) ? deals : []);
                const transformedTemplate: any = transformTemplate(Array.isArray(templates) ? templates : []);
                setDeals(transformedDeal);
                setTemplates(transformedTemplate);
                setFilteredDeals(transformedDeal);
                setFilteredTemplates(transformedTemplate);
                setLoading(false);
            }
            fetchHomeScreenDetails().catch((error) => {
                setHomeError('Deal data could not be loaded. Refresh the page and try again.');
                console.error(error);
            });
        } catch (error) {
            setLoading(false);
            console.error(error);
        }
    }, []);

    return (
        tokenExpired ? <LogoutPopUp openPopup={tokenExpired} /> :
            loading ? <LoadingOutlined style={{ fontSize: "40px", color: "cornflowerblue" }} /> :
                <>
                    {homeError && <Alert type="error" showIcon message="Some deal data could not be loaded" description={homeError} style={{ marginBottom: 16 }} />}
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h1 style={{ color: "#053057" }}>Hi {userContext?.name}</h1>
                            <Tag color="green" style={{borderRadius: '8px'}}>
                            <div style={{display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: '4px', marginBottom: '6px', marginTop: '4px'}}>
                                <div style={{color: 'black', paddingLeft: '4px'}}>HubSpot Data Refresh Status</div>
                                <Tag color="green" bordered={false} icon={<ClockCircleOutlined />}> <span style={{color: 'black'}}>Auto Sync:</span> Updates to your HubSpot deal auto sync here every minute. </Tag>
                                <Tag color="green"  bordered={false} icon={<CalendarOutlined />}> <span style={{color: 'black'}}>Last-Sync:</span> {lastSyncTime}</Tag>
                                {isSyncAllAllowed ? <Tag color='geekblue' icon={<SyncOutlined spin={refreshingDeals}/>} style={{cursor: 'pointer', width: '75%'}} onClick={() => setRefreshDealsModalOpen(true)}> Click to Manually Refresh Deals (Admin Only) </Tag>:
                                <Tag color="green"  bordered={false} icon={<InfoCircleOutlined />}><i>To refresh a specific deal now, click the refresh icon next to the deal in the pricing list below.</i></Tag>
                                }
                            </div>
                            </Tag>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                        {statistics.map((item: any, index: number) => {
                            return (
                                <Card 
                                style={{ flex: 1, height: '150px',
                                    backgroundColor: `${(item['name'] == selectedStatistic) ?'#f0f5ff': ''}`,
                                    transition: `${(item['name'] == selectedStatistic) ?  "transform 0.3s, box-shadow 0.3s" : ""}`,
                                    transform: `${(item['name'] == selectedStatistic) ? "scale(1)": ''}`,
                                    boxShadow: `${(item['name'] == selectedStatistic) ? "0 6px 20px rgba(0, 0, 0, 0.15)": ''}`,
                                 }}
                                key={item['name']} hoverable onClick={()=> handleStatisticFilterApply(item['name'])}>
                                    <div style={{cursor: 'pointer'}} >
                                    <Statistic
                                        title={<div style={{ color: '#20252a9c' }}><h5>{item['name']}</h5></div>}
                                        value={item['value']}
                                        precision={0}
                                        valueStyle={index == 0 ? { color: '#1677ff' } : index == 1 ? { color: 'orange' } : { color: '#F3797E' }}
                                        prefix={index == 0 ? <CloseCircleOutlined /> : index == 1 ? <HourglassOutlined /> : <WarningOutlined />}
                                        suffix=""
                                    />
                                    {index == 2 && <Tag color="orange" bordered={false}>Action Required</Tag>}
                                    </div>
                                </Card>
                            )
                        })}
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Segmented
                        value={selectedTab}
                        style={{ marginBottom: 8 }}
                        onChange={setSelectedTab}
                        options={['My Deals', 'Deals with No Pricing', 'Deals with Pricing', 'All', 'Pricing Templates']}
                    />
                    {selectedTab == 'My Deals' && 
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                        <Checkbox onChange={(e)=> {
                            setHideOpenDeals(!e.target.checked)
                            if(!e.target.checked && hideClosedDeals) setHideClosedDeals(false)
                        }} checked={!hideOpenDeals}>Show Open Deals</Checkbox>
                        <Checkbox onChange={(e)=> {
                            setHideClosedDeals(!e.target.checked)
                            if(!e.target.checked && hideOpenDeals) setHideOpenDeals(false)
                        }} checked={!hideClosedDeals}>Show Closed Deals</Checkbox>
                    </div>
                    }
                    </div>
                    {(selectedTab == 'My Deals' || selectedTab == 'Deals with No Pricing') && <NoPricingDealsTable displayUserSpecificDeals={selectedTab == 'My Deals'} key={`#${selectedTab}`} hideClosed={hideClosedDeals} hideOpen={hideOpenDeals}/>}
                   
                    {(selectedTab == 'All') && <AllPricingDealsTable displayUserSpecificDeals={false} key={`#${selectedTab}`}/>}

                    <br />
                    {(selectedTab == 'My Deals' || selectedTab == 'Deals with Pricing') &&
                        <Card title={'Deals with Pricing'}
                            extra={ 
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    {odeFilter != '' && <Tag bordered={false} color="processing"
                                        onClose={()=>{
                                            setSelectedStatistic('')
                                            setOdeFilter('')
                                        }} closeIcon={<CloseOutlined style={{color: 'inherit'}}/>}>{
                                        odeFilter == 'lock' ? 'Deals Changed After ODE Lock' : 'ODE: Pending' 
                                    }</Tag>
                                    }
                                    <Search
                                        placeholder="Search deals"
                                        value={searchTerm}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        allowClear
                                    />
                                </div>
                            }
                            styles={{body: {paddingTop: `${odeFilter == 'lock'? '8px': ''}`}}}
                        >
                            {
                                odeFilter == 'lock' &&  <Tag color="orange" bordered={false} style={{textWrap: 'wrap', marginBottom: '8px'}}>{`These deals were modified in HubSpot after the ODE lock. Please review the ODE pricing. To update, click the ‘unlock’ icon in the top-right corner of the ODE Pricing Page, then resubmit for ODE locking.`}</Tag>
                            }
                            <Table
                                className='custom-table'
                                dataSource={selectedTab == 'My Deals' ? applyStageFilter(filteredDeals.filter((deal: DataType) => deal.isOwner === true || deal.isPricingContributor === true || deal.canEditPricing === true)) : filteredDeals}
                                columns={columns}
                                scroll={{ x: 'max-content' }}
                                expandable={{
                                    expandedRowRender: (record, index, indent, expanded) => <p style={{ margin: 0 }}><Row gutter={16}><DealEconomicsSummariesTable dealDetails={record} expanded={expanded} /></Row></p>,
                                    rowExpandable: (record) => record.key !== '',
                                    expandedRowKeys: expandedRowKeys,
                                    onExpand: (expanded, record) => {
                                        if (expanded) {
                                            setExpandedRowKeys([...expandedRowKeys, record.key])
                                        } else {
                                            setExpandedRowKeys(expandedRowKeys.filter((k) => k != record.key))
                                        }
                                    }
                                }}
                            />
                        </Card>
                    }
                    {(selectedTab == 'Pricing Templates') &&
                        <Card title={'Pricing Templates'}
                            extra={
                                <div style={{ display: 'flex', justifyContent: 'flex-end', width: "100%" }}>
                                    <Button
                                        title='Create New Pricing Template'
                                        icon={<PlusCircleOutlined />}
                                        onClick={() => handleNavigate('/create-pricing-template')}
                                        style={{
                                            fontSize: '16px',
                                            marginRight: '2.5rem',
                                        }}
                                        className='custom-button'
                                    >
                                        Create New Pricing Template
                                    </Button>
                                    <Search
                                        placeholder="Search templates"
                                        value={templateSearchTerm}
                                        onChange={(e) => handleTemplateSearch(e)}
                                        allowClear
                                    />
                                </div>
                            }
                        >
                            <Table
                                className='custom-table pricing-templates-table'
                                dataSource={filteredTemplates}
                                size="small"
                                columns={templateColumns}
                                scroll={{ x: 'max-content' }}
                            />
                        </Card>
                    }
                    <Modal open={refreshDealsModalOpen} 
                        closable={false} 
                        onCancel={() => setRefreshDealsModalOpen(false)} 
                        onOk={handleAllDealsRefresh}
                        confirmLoading={refreshingDeals}
                    >
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '24px'}}>
                        Refresh From :
                        <DatePicker
                            showTime
                            onChange={(value) => {
                                if( value == null) {
                                    setRefreshTime(null)
                                } else setRefreshTime(dayjs(value).format())
                            }}
                            style={{width: '70%'}}
                        />
                        <Tooltip title={"Leave empty to refresh all deals."}><InfoCircleTwoTone style={{marginTop: '3px'}}/></Tooltip>
                    </div>
                    </Modal>
                </>

    );
};

export default HomePage;
